import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import Lightbox from "../components/Lightbox";
import AngelSparks from "../components/AngelSparks";
import AngelAttributes from "../components/AngelAttributes";
import { api, formatApiError } from "../lib/api";
import { withImageVersion } from "../lib/imageVersion";
import { formatCooldownDate, isCooldownActive } from "../lib/cooldown";
import { notifyNativeShell } from "../lib/nativeBridge";
import { ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { toast } from "sonner";

export default function AngelDetail() {
    const { number } = useParams();
    const navigate = useNavigate();

    const [angel, setAngel] = useState(null);
    const [currentSelection, setCurrentSelection] = useState(null); // string number
    const [nextChangeAllowedAt, setNextChangeAllowedAt] = useState(null); // ISO string
    const [activity, setActivity] = useState(null); // { last24h, last7d } | null
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState("");
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [viewIndex, setViewIndex] = useState(0); // index into views: designs then sketch
    const [swapConfirmOpen, setSwapConfirmOpen] = useState(false);
    const [sparksOrigin, setSparksOrigin] = useState(null); // {x,y} | null
    const confirmBtnRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            try {
                const [angelRes, selRes] = await Promise.all([
                    api.get(`/angels/${number}`),
                    api.get(`/selections/me`),
                ]);
                setAngel(angelRes.data);
                // Default view = the newest design (the angel's current look).
                setViewIndex(Math.max(0, (angelRes.data.designs?.length || 1) - 1));
                setCurrentSelection(selRes.data?.selection?.angelNumber || null);
                setNextChangeAllowedAt(selRes.data?.nextChangeAllowedAt || null);
            } catch (e) {
                setError(formatApiError(e.response?.data?.detail) || "Could not find this Angel.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [number]);

    // Social-proof activity — best-effort, never blocks the page.
    // Runs as a second pass so a slow stats query never delays the angel render.
    useEffect(() => {
        let cancelled = false;
        api.get(`/angels/${number}/activity`)
            .then((res) => {
                if (!cancelled) setActivity(res.data);
            })
            .catch(() => {
                // Silent — the ribbon simply doesn't appear.
            });
        return () => {
            cancelled = true;
        };
    }, [number]);

    const performConfirm = async () => {
        setSwapConfirmOpen(false);
        setConfirming(true);
        setError("");
        try {
            const { data: sel } = await api.post("/selections", { angelNumber: number });
            const wasSwap = currentSelection && currentSelection !== number;
            const nowIso = new Date().toISOString();
            if (sel?.firstMember) {
                toast(`✦ You're their first — Angel ${number} has been waiting for you.`, {
                    duration: 6000,
                    style: {
                        background: "#1a1408",
                        border: "1px solid rgba(207,160,82,0.55)",
                        color: "#e8c887",
                    },
                });
            } else {
                toast.success(wasSwap ? `Switched to Angel ${number}.` : `Angel ${number} is now yours.`);
            }

            // On a swap, fire a `selection_cleared` event FIRST so the native
            // app can drop the old angel from its profile/UI cleanly before
            // the new one arrives. Then the standard `angel_selected` event.
            if (wasSwap) {
                notifyNativeShell({
                    type: "selection_cleared",
                    angel: currentSelection,
                    angelNumber: currentSelection,
                    reason: "swap",
                    clearedAt: nowIso,
                    source: "myangelsquad.com",
                });
            }
            // Broadcast to the native shell so it can silently sync to its
            // own backend. Listener: the Angel Squad Inspirations app's
            // WebView, which listens for type === "angel_selected".
            // `previousAngel` lets the native side handle the swap atomically
            // if it prefers a single event over the cleared+selected pair.
            notifyNativeShell({
                type: "angel_selected",
                angel: number,
                angelNumber: number,
                wasSwap: Boolean(wasSwap),
                firstMember: Boolean(sel?.firstMember),
                previousAngel: wasSwap ? currentSelection : null,
                selectedAt: nowIso,
                source: "myangelsquad.com",
            });
            // Fire sparks from the confirm button (or modal-button focal).
            const btn = confirmBtnRef.current;
            if (btn) {
                const rect = btn.getBoundingClientRect();
                setSparksOrigin({
                    x: rect.left + rect.width / 2,
                    y: rect.top + rect.height / 2,
                });
            } else {
                setSparksOrigin({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
            }
            // Linger on the detail page long enough to enjoy the burst, then go.
            window.setTimeout(() => navigate("/selection", { state: { justSelected: true } }), 1800);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || "Could not save your selection.");
        } finally {
            setConfirming(false);
        }
    };

    const onConfirm = () => {
        // Already has a different angel → require explicit confirmation.
        if (currentSelection && currentSelection !== number) {
            setSwapConfirmOpen(true);
            return;
        }
        performConfirm();
    };

    const cooldownDate = useMemo(() => formatCooldownDate(nextChangeAllowedAt), [nextChangeAllowedAt]);

    if (loading) {
        return <PortalLayout><div className="text-center py-32 font-poetic text-[#9a958a]">drawing back the veil…</div></PortalLayout>;
    }
    if (!angel) {
        return (
            <PortalLayout>
                <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="angel-not-found">
                    This Angel cannot be found.
                </div>
            </PortalLayout>
        );
    }

    const isUnavailable = !angel.isSelectable;
    const isMyAngel = currentSelection && currentSelection === angel.number;
    // The member already picked someone else and is now considering this Angel.
    const isSwapCandidate = currentSelection && currentSelection !== angel.number;
    const cooldownActive = isSwapCandidate && isCooldownActive(nextChangeAllowedAt);
    // Any angel with real artwork AND a real sketch file gets the sketch view
    // (a removed sketch leaves placeholderImageUrl empty until replaced).
    const hasSketch = Boolean(angel.hasArtwork && angel.placeholderImageUrl);
    // Angel's design gallery. Sketch-only angels (sketch uploaded, no design
    // yet) have an empty gallery — the sketch is their single view.
    const galleryDesigns = angel.designs?.length
        ? angel.designs
        : (angel.hasArtwork && angel.mainImageUrl && angel.mainImageUrl !== angel.placeholderImageUrl)
            ? [{ id: "legacy", url: angel.mainImageUrl, version: angel.imageVersion, label: "Design I" }]
            : [];
    const views = [
        ...galleryDesigns.map((d) => ({ ...d, type: "design" })),
        ...(hasSketch ? [{ id: "sketch", type: "sketch", label: "Original sketch" }] : []),
    ];
    const activeView = views[Math.min(viewIndex, Math.max(0, views.length - 1))]
        || { id: "fallback", type: "design", url: angel.mainImageUrl, version: angel.imageVersion, label: "" };
    const isSketchView = activeView.type === "sketch";
    const displayedImageUrl = isSketchView ? angel.placeholderImageUrl : activeView.url;
    const displayedImageVersion = isSketchView ? angel.placeholderImageVersion : activeView.version;

    return (
        <PortalLayout>
            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-12 pb-32" data-testid="angel-detail-page">
                <Link to="/angels" className="inline-flex items-center gap-2 font-label text-[10px] text-[#9a958a] hover:text-[#cfa052] transition-colors mb-12" data-testid="angel-detail-back">
                    <ChevronLeft className="w-4 h-4" /> Back to the grid
                </Link>

                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
                    {/* Left — image (clickable → lightbox) + design/sketch toggle */}
                    <div className="flex flex-col gap-4">
                        <div className="relative">
                        <button
                            type="button"
                            onClick={() => setLightboxOpen(true)}
                            data-testid="angel-detail-open-lightbox"
                            className="group relative w-full aspect-[5/4] overflow-hidden bg-[#151311] border border-white/5 hover:border-[#cfa052]/40 transition-colors duration-500 text-left"
                        >
                            <img
                                src={withImageVersion(displayedImageUrl, displayedImageVersion)}
                                alt={`Angel ${angel.number} — ${activeView.label || "artwork"}`}
                                className={`w-full h-full object-cover ${isUnavailable ? "grayscale opacity-50" : ""}`}
                                data-testid="angel-detail-image"
                            />
                            {/* brass corner arcs — soft decorative frame that gives the
                                detail-page image a "framed artwork" feel and quietly
                                owns the negative space on angels whose composition
                                doesn't fully fill the 5:4 frame (e.g. 046, 049).
                                Pure CSS borders + rounded quarter-circles. */}
                            <span aria-hidden="true" className="pointer-events-none absolute top-2 left-2 w-8 h-8 border-t border-l border-[#cfa052]/45 rounded-tl-[14px]" />
                            <span aria-hidden="true" className="pointer-events-none absolute top-2 right-2 w-8 h-8 border-t border-r border-[#cfa052]/45 rounded-tr-[14px]" />
                            <span aria-hidden="true" className="pointer-events-none absolute bottom-2 left-2 w-8 h-8 border-b border-l border-[#cfa052]/45 rounded-bl-[14px]" />
                            <span aria-hidden="true" className="pointer-events-none absolute bottom-2 right-2 w-8 h-8 border-b border-r border-[#cfa052]/45 rounded-br-[14px]" />
                            <div className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-[#0c0b0a]/60 backdrop-blur-sm border border-white/10 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                <Maximize2 className="w-4 h-4 text-[#cfa052]" />
                            </div>
                        </button>

                        {views.length > 1 && (
                            <>
                                {/* carousel arrows — flip freely through every design + the sketch */}
                                <button
                                    type="button"
                                    aria-label="Previous design"
                                    data-testid="angel-detail-prev-view"
                                    onClick={() => setViewIndex((i) => (i - 1 + views.length) % views.length)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-[#0c0b0a]/70 backdrop-blur-sm border border-white/10 rounded-full text-[#cfa052] hover:border-[#cfa052]/60 transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Next design"
                                    data-testid="angel-detail-next-view"
                                    onClick={() => setViewIndex((i) => (i + 1) % views.length)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-[#0c0b0a]/70 backdrop-blur-sm border border-white/10 rounded-full text-[#cfa052] hover:border-[#cfa052]/60 transition-colors"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </>
                        )}
                        </div>

                        {views.length > 1 && (
                            <div
                                className="flex flex-wrap gap-2 self-start rounded-full"
                                role="tablist"
                                aria-label="Choose artwork view"
                                data-testid="angel-detail-view-toggle"
                            >
                                {views.map((v, idx) => (
                                    <button
                                        key={v.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={idx === viewIndex}
                                        onClick={() => setViewIndex(idx)}
                                        data-testid={v.type === "sketch" ? "angel-detail-view-sketch" : `angel-detail-view-design-${idx + 1}`}
                                        className={`font-label text-[10px] tracking-[0.25em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                                            idx === viewIndex
                                                ? "bg-[#cfa052] border-[#cfa052] text-[#0c0b0a]"
                                                : "border-white/10 bg-[#151311] text-[#9a958a] hover:text-[#e8e4db]"
                                        }`}
                                    >
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {hasSketch && (
                            <p className="font-poetic text-xs text-[#6e6a62] max-w-md">
                                {isSketchView
                                    ? galleryDesigns.length
                                        ? "The initial automatic-drawing sketch — the moment this Angel first arrived."
                                        : "The original sketch — this Angel just landed; the designed version is on its way."
                                    : galleryDesigns.length > 1
                                        ? `${activeView.label} of ${galleryDesigns.length} — this Angel's look is refined and improved over time.`
                                        : "The redesigned, colour-printed version — ready for t-shirts, pendants and other things Angel."}
                            </p>
                        )}
                    </div>

                    {/* Right — content */}
                    <div className="flex flex-col justify-center">
                        <span className="font-label text-[11px] text-[#cfa052]">Angel № {angel.number}</span>
                        <h1 className="mt-4 font-serif-display text-6xl md:text-7xl tracking-tighter text-[#e8e4db]" data-testid="angel-detail-number">
                            {angel.number}
                        </h1>
                        <p className="mt-8 font-poetic text-xl text-[#9a958a] leading-relaxed" data-testid="angel-detail-status">
                            {isUnavailable
                                ? angel.statusText
                                : angel.approvedAttributes?.length
                                    ? `${angel.approvedAttributes.join(" · ")}.`
                                    : (angel.openedForSelectionAt && Date.now() - new Date(angel.openedForSelectionAt).getTime() < 14 * 24 * 3600 * 1000)
                                        ? "Newly arrived — be the first to suggest their attributes below."
                                        : "Attributes being verified."}
                        </p>

                        {!isUnavailable && !isMyAngel && activity && (activity.last24h >= 2 || activity.last7d >= 2) && (
                            <div
                                className="mt-6 inline-flex items-center gap-2.5 self-start"
                                data-testid="angel-detail-activity"
                            >
                                <span
                                    aria-hidden="true"
                                    className="relative flex items-center justify-center w-2 h-2"
                                >
                                    <span className="absolute inset-0 rounded-full bg-[#cfa052] opacity-60 animate-ping" />
                                    <span className="relative w-2 h-2 rounded-full bg-[#cfa052]" />
                                </span>
                                <span className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052]">
                                    {activity.last24h >= 2
                                        ? `${activity.last24h} members chose this Angel today`
                                        : `${activity.last7d} members chose this Angel this week`}
                                </span>
                            </div>
                        )}

                        {!isUnavailable && !isMyAngel && !isSwapCandidate && (
                            <p className="mt-6 font-poetic text-sm text-[#6e6a62] max-w-md">
                                Tap <span className="text-[#e8e4db]">Confirm selection</span> to claim this Angel as yours. You can change your Angel once every 7 days.
                            </p>
                        )}

                        {isSwapCandidate && !cooldownActive && (
                            <p className="mt-6 font-poetic text-sm text-[#cfa052] max-w-md" data-testid="angel-detail-swap-hint">
                                You currently have Angel {currentSelection}. Confirming will switch your selection to Angel {angel.number}.
                            </p>
                        )}

                        {isSwapCandidate && cooldownActive && (
                            <p className="mt-6 font-poetic text-sm text-[#9a958a] max-w-md" data-testid="angel-detail-cooldown-hint">
                                You picked Angel {currentSelection} recently. The next change is available on <span className="text-[#e8e4db]">{cooldownDate}</span>.
                            </p>
                        )}

                        {error && (
                            <div className="mt-6 font-poetic text-sm text-[#d97c6a]" data-testid="angel-detail-error">
                                {error}
                            </div>
                        )}

                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            {isUnavailable ? (
                                <button
                                    disabled
                                    data-testid="angel-detail-unavailable-btn"
                                    className="bg-[#2a2723] text-[#6e6a62] cursor-not-allowed px-8 py-4 font-label text-xs tracking-widest rounded-sm"
                                >
                                    Unavailable
                                </button>
                            ) : isMyAngel ? (
                                <Link
                                    to="/selection"
                                    data-testid="angel-detail-view-selection"
                                    className="bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300"
                                >
                                    View my Angel
                                </Link>
                            ) : isSwapCandidate && cooldownActive ? (
                                <button
                                    type="button"
                                    onClick={() =>
                                        toast(`Your Angel is sealed until ${cooldownDate}.`, {
                                            description: `You currently have Angel ${currentSelection}. You can change once every 7 days.`,
                                        })
                                    }
                                    aria-disabled="true"
                                    data-testid="angel-detail-cooldown-btn"
                                    className="bg-[#2a2723] text-[#6e6a62] cursor-not-allowed px-8 py-4 font-label text-xs tracking-widest rounded-sm hover:bg-[#332f2a] transition-colors"
                                >
                                    Available {cooldownDate}
                                </button>
                            ) : (
                                <button
                                    onClick={onConfirm}
                                    disabled={confirming}
                                    data-testid="angel-detail-confirm-btn"
                                    className="bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300 disabled:opacity-60"
                                >
                                    {confirming ? "Sealing…" : isSwapCandidate ? `Switch to Angel ${angel.number}` : "Confirm selection"}
                                </button>
                            )}

                            <Link
                                to="/angels"
                                data-testid="angel-detail-back-btn"
                                className="border border-white/20 hover:border-[#e8e4db]/60 text-[#e8e4db] px-8 py-4 font-label text-xs tracking-widest rounded-sm transition-colors duration-300"
                            >
                                Back to grid
                            </Link>
                        </div>

                        <AngelAttributes angelNumber={angel.number} hidden={isUnavailable} />
                    </div>
                </div>
            </section>
            {lightboxOpen && (
                <Lightbox
                    angel={angel}
                    imageUrl={displayedImageUrl}
                    imageVersion={displayedImageVersion}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
            {sparksOrigin && (
                <AngelSparks
                    origin={sparksOrigin}
                    onDone={() => setSparksOrigin(null)}
                />
            )}
            {swapConfirmOpen && (
                <div
                    className="fixed inset-0 z-[100] bg-[#0c0b0a]/85 backdrop-blur-sm flex items-center justify-center px-6"
                    role="dialog"
                    aria-modal="true"
                    data-testid="swap-confirm-modal"
                >
                    <div className="max-w-md w-full bg-[#151311] border border-[#cfa052]/30 rounded-sm p-8 shadow-2xl">
                        <span className="font-label text-[10px] tracking-[0.3em] uppercase text-[#cfa052]">
                            Switch your Angel?
                        </span>
                        <h2
                            className="mt-3 font-serif-display text-3xl text-[#e8e4db] tracking-tight"
                            data-testid="swap-confirm-title"
                        >
                            Angel {currentSelection} <span className="text-[#6e6a62]">→</span> Angel {angel.number}
                        </h2>
                        <p className="mt-4 font-poetic text-[#9a958a] text-base leading-relaxed">
                            This will replace your current Angel.
                            <span className="block mt-2 text-sm text-[#6e6a62]">
                                You can change again in 7 days.
                            </span>
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-end">
                            <button
                                type="button"
                                onClick={() => setSwapConfirmOpen(false)}
                                data-testid="swap-confirm-cancel"
                                className="border border-white/20 hover:border-[#e8e4db]/60 text-[#e8e4db] px-6 py-3 font-label text-[11px] tracking-widest uppercase rounded-sm transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={performConfirm}
                                disabled={confirming}
                                data-testid="swap-confirm-confirm"
                                className="bg-[#cfa052] hover:bg-[#e0b569] disabled:opacity-60 text-[#0c0b0a] px-6 py-3 font-label text-[11px] tracking-widest uppercase rounded-sm transition-colors"
                            >
                                {confirming ? "Switching…" : "Confirm switch"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PortalLayout>
    );
}
